import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import FadeIn from "../components/FadeIn";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  travel_dates: "",
  group_size: "",
  budget_range: "",
  interests: "",
};

const PlanJourney = () => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Your name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "A valid email is required.";
    if (!form.travel_dates.trim()) return "Travel dates help us design properly.";
    if (!form.group_size.trim()) return "Group size is required.";
    if (!form.budget_range) return "Please select a budget range.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setDone(true);
      toast.success("Thank you. We will be in touch shortly.");
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-28 md:pt-36" data-testid="plan-page">
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-end">
          <div className="md:col-span-7">
            <FadeIn>
              <div className="overline">Begin</div>
              <h1 className="mt-5 font-serif text-5xl md:text-7xl text-bone tracking-tight leading-[1.02]">
                Plan Your <span className="italic text-gold">Journey</span>
              </h1>
            </FadeIn>
          </div>
          <div className="md:col-span-5">
            <FadeIn delay={140}>
              <p className="text-bone/65 font-light text-lg leading-relaxed">
                We take on a limited number of clients each month to ensure quality and attention.
                Share a few details below. We will respond personally.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="container-editorial">
          {done ? (
            <FadeIn>
              <div className="max-w-2xl mx-auto text-center py-20" data-testid="inquiry-success">
                <div className="overline mb-6">Received</div>
                <h2 className="font-serif text-4xl md:text-5xl text-bone tracking-tight leading-[1.1]">
                  Thank you.<br />
                  <span className="italic text-gold">We will be in touch shortly.</span>
                </h2>
                <p className="mt-8 text-bone/60 font-light">
                  Every journey begins with a conversation. Expect a thoughtful reply — not a template.
                </p>
                <button
                  onClick={() => setDone(false)}
                  className="mt-12 link-underline text-[12px] tracking-[0.22em] uppercase text-bone/70 hover:text-bone"
                  data-testid="inquiry-new"
                >
                  Submit another inquiry →
                </button>
              </div>
            </FadeIn>
          ) : (
            <FadeIn>
              <form onSubmit={onSubmit} className="max-w-2xl mx-auto" data-testid="inquiry-form" noValidate>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
                  <Field label="Name" htmlFor="name">
                    <input
                      id="name"
                      type="text"
                      className="editorial-input"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      data-testid="input-name"
                      autoComplete="name"
                    />
                  </Field>

                  <Field label="Email" htmlFor="email">
                    <input
                      id="email"
                      type="email"
                      className="editorial-input"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) => update("email", e.target.value)}
                      data-testid="input-email"
                      autoComplete="email"
                    />
                  </Field>

                  <Field label="Travel Dates" htmlFor="travel_dates">
                    <input
                      id="travel_dates"
                      type="text"
                      className="editorial-input"
                      placeholder="e.g., April 12 – April 22, 2026"
                      value={form.travel_dates}
                      onChange={(e) => update("travel_dates", e.target.value)}
                      data-testid="input-dates"
                    />
                  </Field>

                  <Field label="Group Size" htmlFor="group_size">
                    <input
                      id="group_size"
                      type="text"
                      className="editorial-input"
                      placeholder="e.g., 2 adults"
                      value={form.group_size}
                      onChange={(e) => update("group_size", e.target.value)}
                      data-testid="input-group"
                    />
                  </Field>

                  <div className="md:col-span-2">
                    <label className="overline mb-4 block">Budget Range</label>
                    <Select value={form.budget_range} onValueChange={(v) => update("budget_range", v)}>
                      <SelectTrigger
                        className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-4 text-bone hover:border-gold focus:border-gold focus:ring-0 h-auto text-base font-sans data-[placeholder]:text-bone/30"
                        data-testid="input-budget"
                      >
                        <SelectValue placeholder="Select a budget range" />
                      </SelectTrigger>
                      <SelectContent className="bg-ink border border-white/10 rounded-none text-bone">
                        <SelectItem value="$15,000+" data-testid="budget-15k">$15,000+</SelectItem>
                        <SelectItem value="$25,000+" data-testid="budget-25k">$25,000+</SelectItem>
                        <SelectItem value="$50,000+" data-testid="budget-50k">$50,000+</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Field label="Interests / Notes" htmlFor="interests">
                      <textarea
                        id="interests"
                        rows={5}
                        className="editorial-input resize-none"
                        placeholder="Tell us what you would like to feel, avoid, and experience."
                        value={form.interests}
                        onChange={(e) => update("interests", e.target.value)}
                        data-testid="input-interests"
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <p className="text-bone/50 text-sm font-light max-w-sm">
                    By sending, you agree to receive a personal reply from Mensorio.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center gap-4 bg-gold text-ink px-10 py-5 text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-gold-light transition-colors duration-500 disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="submit-inquiry"
                  >
                    {submitting ? "Sending…" : "Send Inquiry"}
                    <span className="w-8 h-px bg-ink/60" />
                  </button>
                </div>
              </form>
            </FadeIn>
          )}
        </div>
      </section>

      <section className="py-24 md:py-32 border-t border-white/5">
        <div className="container-editorial text-center max-w-3xl mx-auto">
          <FadeIn>
            <p className="font-serif italic text-3xl md:text-4xl text-bone/85 tracking-tight leading-[1.2]">
              Every journey begins with <span className="text-gold">a conversation.</span>
            </p>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="overline mb-4 block">
      {label}
    </label>
    {children}
  </div>
);

export default PlanJourney;
